import { useState, useEffect, useMemo } from 'react';
import { SatelliteRecord, SatellitePoint, GroundTrack } from '../types/satellite';
import { OrbitalMechanics } from '../services/orbitalMechanics';
import { DEFAULT_CONFIG } from '../config/satelliteGroups';

interface UseSatelliteVisualizationReturn {
  globePoints: SatellitePoint[];
  groundTracks: GroundTrack[];
  currentTime: Date;
}

export function useSatelliteVisualization(
  satellites: SatelliteRecord[],
  showTracks: boolean = false
): UseSatelliteVisualizationReturn {
  
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Update time every 2 seconds for real-time feel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, DEFAULT_CONFIG.updateIntervalMs);
    
    return () => clearInterval(interval);
  }, []);
  
  // Calculate current satellite positions for globe visualization
  const globePoints = useMemo(() => {
    return satellites
      .map((satellite): SatellitePoint | null => {
        const position = OrbitalMechanics.propagateToGeodetic(satellite.satrec, currentTime);
        
        if (!position) return null;
        
        // Determine color based on altitude or other factors
        const color = getColorForSatellite(satellite, position.altKm);
        
        return {
          name: satellite.name,
          lat: position.lat,
          lng: position.lon,
          size: 0.45,
          color,
          alt: Math.max(0.001, position.altKm / 6371), // Normalized for Globe
        };
      })
      .filter((point): point is SatellitePoint => point !== null);
  }, [satellites, currentTime]);
  
  // Calculate ground tracks if enabled
  const groundTracks = useMemo(() => {
    if (!showTracks) return [];
    
    const { maxSatellites, stepSec, steps } = DEFAULT_CONFIG.groundTracks;
    const trackedSatellites = satellites.slice(0, Math.min(satellites.length, maxSatellites));
    
    return trackedSatellites
      .map((satellite): GroundTrack | null => {
        const path = OrbitalMechanics.generateGroundTrack(
          satellite,
          currentTime,
          steps,
          stepSec
        );
        
        return path.length > 0 ? {
          name: satellite.name,
          path,
        } : null;
      })
      .filter((track): track is GroundTrack => track !== null);
  }, [satellites, currentTime, showTracks]);
  
  return {
    globePoints,
    groundTracks,
    currentTime,
  };
}

/**
 * Determine satellite color based on characteristics
 */
function getColorForSatellite(_satellite: SatelliteRecord, altitudeKm: number): string {
  // Color coding based on altitude
  if (altitudeKm < 500) {
    return '#ff4444'; // Red for very low orbit (ISS, etc.)
  } else if (altitudeKm < 1000) {
    return '#44ff44'; // Green for LEO
  } else if (altitudeKm < 20000) {
    return '#4444ff'; // Blue for MEO
  } else {
    return '#ffff44'; // Yellow for GEO
  }
}

/**
 * Hook for managing satellite selection and focus
 */
export function useSatelliteSelection() {
  const [selectedSatellite, setSelectedSatellite] = useState<SatelliteRecord | null>(null);
  const [focusedSatellite, setFocusedSatellite] = useState<string | null>(null);
  
  const selectSatellite = (satellite: SatelliteRecord | null) => {
    setSelectedSatellite(satellite);
    setFocusedSatellite(satellite?.name ?? null);
  };
  
  const clearSelection = () => {
    setSelectedSatellite(null);
    setFocusedSatellite(null);
  };
  
  return {
    selectedSatellite,
    focusedSatellite,
    selectSatellite,
    clearSelection,
  };
}